import React, { useState } from 'react';
import { adminEndpoints } from '../services/api';
import { Button, TextField, Box, Typography, Alert } from '@mui/material';

interface BulkCreateTeamNamesProps {
  onSuccess?: () => void;
}

const parseTeamNames = (input: string): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];

  for (const line of input.split(/\r?\n/)) {
    const name = line.trim();
    if (!name) continue;

    const key = name.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    names.push(name);
  }

  return names;
};

const BulkCreateTeamNames: React.FC<BulkCreateTeamNamesProps> = ({ onSuccess }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    const names = parseTeamNames(input);

    if (names.length === 0) {
      setError('Enter at least one team name (one per line)');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setIsLoading(true);

      let created = 0;
      const failures: string[] = [];

      for (const teamName of names) {
        try {
          await adminEndpoints.createTeamName({ teamName });
          created += 1;
        } catch {
          failures.push(teamName);
        }
      }

      if (created === 0) {
        throw new Error('Failed to create team names');
      }

      if (failures.length > 0) {
        setSuccess(`Created ${created} team name${created === 1 ? '' : 's'}. Failed: ${failures.join(', ')}`);
      } else {
        setSuccess(`Successfully created ${created} team name${created === 1 ? '' : 's'}`);
        setInput('');
      }

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team names');
    } finally {
      setIsLoading(false);
    }
  };

  const previewCount = parseTeamNames(input).length;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter one team name per line. Duplicate and empty lines are ignored.
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={12}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={'Red Dragons\nBlue Sharks\nGreen Giants'}
        variant="outlined"
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={isLoading || previewCount === 0}
        >
          {isLoading
            ? 'Creating...'
            : previewCount === 0
              ? 'Create Team Names'
              : `Create ${previewCount} Team Name${previewCount === 1 ? '' : 's'}`}
        </Button>
        {previewCount > 0 && !isLoading && (
          <Typography variant="body2" color="text.secondary">
            {previewCount} name{previewCount === 1 ? '' : 's'} ready
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default BulkCreateTeamNames;
